import { Directive, computed, input } from '@angular/core';
import { STAT_STRIP_NUMERALS, type StatStripNumerals } from '@groove/core';

// The numerals vocabulary is owned by @groove/core (framework-neutral, so the
// coverage engine can read it without pulling in @angular/core). Re-exported
// here so consumers can keep importing it from the directive.
export { STAT_STRIP_NUMERALS, type StatStripNumerals };

/**
 * Credibility / stat strip (component tier). A rule-bounded flex row of stat
 * items; each item is plain markup — `<div><b>12</b> open positions</div>` —
 * styled by the strip CSS (no per-item directive; YAGNI). The strip draws decor
 * hairlines top + bottom; the numerals are set in the display face at large
 * size; the labels are body-face muted.
 *
 * Numeral color is a per-instance style hook, NOT a gated tone axis. This is a
 * deliberate decision (see groove-stat-strip.coverage.ts in @groove/core): the
 * strip/numeral split means a tone data-attr, the large-numeral contrast read,
 * and the strip border cannot all live on one element for the validation
 * harness's per-cell gate. So the numerals default to fg-default (the neutral
 * ink numerals) and the decor-colored look is the `numerals="decor"` option,
 * which binds --_stat-numeral-color to the decor role on the host. decor is
 * graded >=3:1 on every ladder step and the numerals are large display text, so
 * decor numerals clear the large-text floor by token construction.
 */
@Directive({
  selector: '[grooveStatStrip]',
  standalone: true,
  host: {
    class: 'groove-stat-strip',
    '[style.--_stat-numeral-color]': 'numeralColor()',
  },
})
export class GrooveStatStripDirective {
  readonly numerals = input<StatStripNumerals>('default');

  // 'default' leaves the CSS fallback (fg-default) in place; 'decor' re-binds
  // the private numeral-color var on the host so every <b> inherits it.
  protected readonly numeralColor = computed(() =>
    this.numerals() === 'decor' ? 'var(--groove-color-decor)' : null,
  );
}
